import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Briefcase, Clock, FileText, History, Plus, User,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHoras, hoyISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/hooks/useEmpresa";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HorarioTramosEditor, horarioParaGuardarTramos, resumenHorarioTramos, erroresSolapeTramos } from "@/components/HorarioTramosEditor";
import { HorarioModoConfig } from "@/components/HorarioModoConfig";
import { MODO_PATRON_TURNOS, mensajeObjetivoFlexible, resumenModoHorario } from "@/lib/horarioModos";
import { horarioDepartamento, horarioEditableDesdeOrigen } from "@/lib/horarioEditable";
import VacacionesResumen from "@/components/VacacionesResumen";
import AsuntosPropiosResumen from "@/components/AsuntosPropiosResumen";

function SeccionCard({ icon: Icon, titulo, children, accent = "indigo" }) {
  const accents = {
    indigo: "border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-white",
    sky: "border-sky-100 bg-gradient-to-br from-sky-50/50 to-white",
    rose: "border-rose-100 bg-gradient-to-br from-rose-50/40 to-white",
  };
  return (
    <section className={`rounded-2xl border p-5 shadow-sm ${accents[accent] || accents.indigo}`}>
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white shadow-sm">
          <Icon className="h-4 w-4 text-indigo-600" strokeWidth={1.5} />
        </span>
        <h2 className="font-display text-base font-semibold text-zinc-900">{titulo}</h2>
      </div>
      {children}
    </section>
  );
}

function Campo({ label, value, sub }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="text-sm font-semibold text-zinc-900">{value || "—"}</p>
      {sub && <p className="text-[11px] text-zinc-400">{sub}</p>}
    </div>
  );
}

function DiasBadges({ dias }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {(dias || []).map((d) => (
        <span
          key={d.clave || d.dia}
          className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
            d.activo ? "bg-teal-600 text-white" : "text-zinc-400"
          }`}
          title={d.dia}
        >
          {d.dia}
        </span>
      ))}
    </div>
  );
}

export default function FichaEmpleado() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { empresa } = useEmpresa();
  const esAdmin = user?.rol === "admin";
  const [ficha, setFicha] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [deps, setDeps] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [tiposContrato, setTiposContrato] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    vigente_desde: hoyISO(),
    motivo: "",
    tipo_contrato_id: "none",
    departamento_id: "none",
    puesto_id: "none",
    fecha_inicio_contrato: "",
  });
  const [antiguedadOriginal, setAntiguedadOriginal] = useState("");
  const [confirmAntiguedadOpen, setConfirmAntiguedadOpen] = useState(false);
  const [usarHorario, setUsarHorario] = useState(false);
  const [horarioTramos, setHorarioTramos] = useState([]);
  const [horarioModo, setHorarioModo] = useState(horarioEditableDesdeOrigen(null).modoConfig);
  const [turnos, setTurnos] = useState([]);
  const [arrastreForm, setArrastreForm] = useState({ dias: "0", motivo: "" });
  const [guardandoArrastre, setGuardandoArrastre] = useState(false);
  const [usadosForm, setUsadosForm] = useState({ vacaciones: "0", asuntos: "0", motivo: "" });
  const [guardandoUsados, setGuardandoUsados] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [f, h, d, p, t, turn] = await Promise.all([
        api.get(`/usuarios/${id}/ficha`),
        api.get(`/usuarios/${id}/historial-condiciones`),
        api.get("/departamentos"),
        api.get("/puestos"),
        api.get("/tipos-contrato"),
        api.get("/turnos").catch(() => ({ data: [] })),
      ]);
      setFicha(f.data);
      if (f.data?.rol === "admin" && empresa?.gestionar_gerencia) {
        toast.info("La cuenta de administrador solo permite cambiar la contraseña desde Empleados.");
        navigate("/admin/empleados");
        return;
      }
      setHistorial(h.data);
      setDeps(d.data);
      setPuestos(p.data);
      setTiposContrato(t.data);
      setTurnos(turn.data || []);
      const vac = f.data?.vacaciones;
      if (vac) {
        setArrastreForm({
          dias: String(vac.arrastre ?? 0),
          motivo: vac.arrastre_origen === "manual" ? (vac.arrastre_motivo || "") : "",
        });
      }
      const asp = f.data?.asuntos_propios;
      setUsadosForm({
        vacaciones: String(vac?.usados_ajuste ?? 0),
        asuntos: String(asp?.usados_ajuste ?? 0),
        motivo: vac?.usados_ajuste_motivo || asp?.ajuste_motivo || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/admin/empleados");
    }
  }, [id, navigate, empresa?.gestionar_gerencia]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarArrastre = async () => {
    if (!arrastreForm.motivo.trim() || arrastreForm.motivo.trim().length < 3) {
      toast.error("Indica un motivo (mín. 3 caracteres)");
      return;
    }
    const anio = ficha?.vacaciones?.anio || new Date().getFullYear();
    setGuardandoArrastre(true);
    try {
      await api.put(`/usuarios/${id}/vacaciones-arrastre`, {
        anio,
        dias: Number(arrastreForm.dias),
        motivo: arrastreForm.motivo.trim(),
      });
      toast.success("Arrastre de vacaciones actualizado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoArrastre(false);
    }
  };

  const quitarArrastre = async () => {
    const anio = ficha?.vacaciones?.anio || new Date().getFullYear();
    setGuardandoArrastre(true);
    try {
      await api.delete(`/usuarios/${id}/vacaciones-arrastre/${anio}`);
      toast.success("Override eliminado; se usa el cálculo automático");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoArrastre(false);
    }
  };

  const guardarUsados = async () => {
    const anio = ficha?.vacaciones?.anio || new Date().getFullYear();
    setGuardandoUsados(true);
    try {
      await api.put(`/usuarios/${id}/dias-usados`, {
        anio,
        vacaciones_usados: Number(usadosForm.vacaciones || 0),
        asuntos_usados: Number(usadosForm.asuntos || 0),
        motivo: usadosForm.motivo.trim() || "Consumo previo a la incorporación en Ventura HR",
      });
      toast.success("Días ya usados actualizados (solo este año)");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoUsados(false);
    }
  };

  const quitarUsados = async () => {
    const anio = ficha?.vacaciones?.anio || new Date().getFullYear();
    setGuardandoUsados(true);
    try {
      await api.delete(`/usuarios/${id}/dias-usados/${anio}`);
      toast.success("Ajuste de días usados eliminado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoUsados(false);
    }
  };

  const u = ficha?.usuario;
  const c = ficha?.condiciones_actuales;

  const aplicarHorarioEditable = (origen) => {
    const editable = horarioEditableDesdeOrigen(origen);
    setHorarioTramos(editable.tramos);
    setHorarioModo(editable.modoConfig);
  };

  const onToggleHorarioIndividual = (checked) => {
    setUsarHorario(checked);
    if (checked) {
      const depId = form.departamento_id !== "none" ? form.departamento_id : (u?.departamento_id ? String(u.departamento_id) : "none");
      const origen = u?.horario || horarioDepartamento(deps, depId);
      aplicarHorarioEditable(origen);
    }
  };

  const abrirNuevas = () => {
    const alta = u?.fecha_inicio_contrato || "";
    setAntiguedadOriginal(alta);
    setForm({
      vigente_desde: hoyISO(),
      motivo: "",
      tipo_contrato_id: u?.tipo_contrato_id ? String(u.tipo_contrato_id) : "none",
      departamento_id: u?.departamento_id ? String(u.departamento_id) : "none",
      puesto_id: u?.puesto_id ? String(u.puesto_id) : "none",
      fecha_inicio_contrato: alta,
    });
    const h = u?.horario;
    setUsarHorario(!!h);
    aplicarHorarioEditable(h || horarioDepartamento(deps, u?.departamento_id ? String(u.departamento_id) : "none"));
    setNuevaOpen(true);
  };

  const enviarCondiciones = async (confirmarAntiguedad = false) => {
    if (!form.motivo.trim() || form.motivo.trim().length < 3) {
      toast.error("Indica el motivo del cambio");
      return;
    }
    if (usarHorario && horarioModo.modo !== MODO_PATRON_TURNOS) {
      const errs = erroresSolapeTramos(horarioTramos);
      if (errs.length) {
        toast.error(errs[0]);
        return;
      }
    }
    if (usarHorario) {
      const avisoObjetivo = mensajeObjetivoFlexible(horarioModo);
      if (avisoObjetivo && !window.confirm(avisoObjetivo)) return;
    }
    const cambioAntiguedad = (form.fecha_inicio_contrato || "") !== (antiguedadOriginal || "");
    if (cambioAntiguedad && !confirmarAntiguedad) {
      setConfirmAntiguedadOpen(true);
      return;
    }
    setGuardando(true);
    try {
      const payload = {
        vigente_desde: form.vigente_desde,
        motivo: form.motivo.trim(),
      };
      if (form.tipo_contrato_id !== "none") payload.tipo_contrato_id = Number(form.tipo_contrato_id);
      if (form.departamento_id !== "none") payload.departamento_id = Number(form.departamento_id);
      if (form.puesto_id !== "none") payload.puesto_id = Number(form.puesto_id);
      if (cambioAntiguedad) {
        payload.fecha_inicio_contrato = form.fecha_inicio_contrato || null;
        payload.confirmar_cambio_antiguedad = true;
      }
      if (usarHorario) payload.horario = horarioParaGuardarTramos(horarioTramos, horarioModo);
      const { data } = await api.post(`/usuarios/${id}/condiciones`, payload);
      toast.success("Cambio de contrato registrado en el historial");
      (data?.avisos || []).forEach((a) => toast.warning(a, { duration: 8000 }));
      setConfirmAntiguedadOpen(false);
      setNuevaOpen(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const guardarCondiciones = () => enviarCondiciones(false);

  if (!ficha) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-indigo-600" />
      </div>
    );
  }

  const nombre = `${u.nombre} ${u.apellidos}`.trim();
  const horasSem = c?.horas_laborales_semana;
  const horasAnuales = c?.horas_anuales_max;

  return (
    <div className="space-y-6" data-testid="ficha-empleado">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" asChild className="mt-1 shrink-0">
            <Link to="/admin/empleados"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <PageHeader
              title={`Las condiciones actuales de ${u.nombre || nombre}`}
              subtitle={
                ficha.vigente_desde
                  ? `Efectivas desde el ${fmtFecha(ficha.vigente_desde)}`
                  : "Condiciones laborales vigentes"
              }
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {esAdmin && (
            <Button onClick={abrirNuevas} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="mr-1.5 h-4 w-4" /> Cambio de contrato
            </Button>
          )}
          <Button variant="outline" onClick={() => setHistOpen(true)}>
            <History className="mr-1.5 h-4 w-4" /> Historial
          </Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <SeccionCard icon={User} titulo="Información de contrato" accent="indigo">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              label="Inicio de contrato (antigüedad)"
              value={c?.fecha_inicio_contrato ? fmtFecha(c.fecha_inicio_contrato) : "—"}
              sub="Prorrateo del cupo del año. El arrastre de vacaciones empieza en el año del primer picado."
            />
            <Campo label="Tipo de contrato" value={c?.tipo_contrato_nombre} />
            <Campo label="Situación" value={c?.situacion === "baja" ? "Baja" : "Activo"} />
            <Campo
              label="Departamento"
              value={c?.departamento_nombre}
              sub={c?.centro_nombre}
            />
          </div>
        </SeccionCard>

        <SeccionCard icon={Briefcase} titulo="Puesto de trabajo" accent="sky">
          <div className="grid gap-4">
            <Campo label="Puesto actual" value={c?.puesto_nombre} />
            <Campo label="DNI/NIE" value={u.dni_nie} />
            <Campo label="Fecha de nacimiento" value={u.fecha_nacimiento ? fmtFecha(u.fecha_nacimiento) : "—"} />
            <Campo
              label="Avisar a compañeros del centro"
              value={u.cumpleanos_avisar_companeros ? "Sí" : "No"}
            />
            <Campo label="Email" value={u.email} />
            <Campo label="Teléfono" value={u.telefono} />
          </div>
        </SeccionCard>

        <SeccionCard icon={Clock} titulo="Tiempo" accent="rose">
          <div className="grid gap-5 sm:grid-cols-3">
            <Campo label="Tipo de jornada laboral" value={c?.tipo_jornada} />
            <Campo
              label="Horas laborales"
              value={horasSem != null ? `${horasSem}h / semana` : "—"}
            />
            <div className="space-y-1">
              <p className="text-xs text-zinc-500">Jornada semanal</p>
              <DiasBadges dias={c?.dias_laborables} />
            </div>
            <Campo
              label="Días laborables del año"
              value={c?.dias_laborables_anio != null ? c.dias_laborables_anio : "—"}
              sub={
                c?.dias_laborables_detalle
                  ? `${c.dias_laborables_detalle.anio}: ${
                      c.dias_laborables_detalle.modo_horario === "flexible"
                        ? "objetivo L–V − festivos"
                        : "horario − festivos"
                    }${
                      c.dias_laborables_detalle.dias_festivo_anio
                        ? ` (${c.dias_laborables_detalle.dias_festivo_anio} fest.)`
                        : ""
                    }`
                  : "Según horario y festivos"
              }
            />
            <Campo
              label="Días laborables restantes"
              value={c?.dias_laborables_restantes != null ? c.dias_laborables_restantes : "—"}
              sub={
                c?.dias_laborables_detalle?.desde
                  ? `Desde hoy hasta el 31/12/${c.dias_laborables_detalle.anio} (horario − festivos)`
                  : "Hasta fin de año"
              }
            />
            <Campo
              label="Horas anuales máx."
              value={horasAnuales != null ? `${horasAnuales.toLocaleString("es-ES")} h/año` : "—"}
              sub={
                c?.horas_anuales_detalle
                  ? (() => {
                      const d = c.horas_anuales_detalle;
                      const partes = [];
                      if (d.dias_festivo_descontados) {
                        partes.push(`${d.dias_festivo_descontados} festivos`);
                      }
                      if (d.dias_vacaciones_descontados) {
                        partes.push(
                          `${d.dias_vacaciones_descontados} d vac. (${d.vacaciones_modo_calculo === "habiles" ? "hábiles" : "naturales"})`
                        );
                      }
                      const base = partes.length
                        ? `${d.anio_calculo} completo: horario − ${partes.join(" − ")}`
                        : `${d.anio_calculo} completo: según horario del año`;
                      if (d.horas_desde_registro != null && d.desde_registro) {
                        return `${base}. Desde primer picado (${fmtFecha(d.desde_registro)}): ${Number(d.horas_desde_registro).toLocaleString("es-ES")} h`;
                      }
                      if (!d.fecha_primer_picado) {
                        return `${base}. Aún sin picados: el cómputo en Control Horario empezará con el primero.`;
                      }
                      return base;
                    })()
                  : "Año natural − festivos − cupo vacaciones"
              }
            />
            <Campo
              label="Horas semanales máx."
              value={horasSem != null ? `${horasSem}h / semana` : "—"}
            />
            <Campo
              label="Extra disponible (año)"
              value={fmtHoras(ficha.extra_disponible?.disponible_segundos || 0)}
              sub="Horas extra pendientes de compensar"
            />
          </div>
        </SeccionCard>

        <SeccionCard icon={FileText} titulo="Vacaciones" accent="indigo">
          <VacacionesResumen vacaciones={ficha.vacaciones} />
          {esAdmin && ficha.vacaciones && (
            <div className="mt-4 space-y-3 border-t border-indigo-100 pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Ajuste manual de arrastre ({ficha.vacaciones.anio || new Date().getFullYear()})
              </p>
              <p className="text-xs text-zinc-500">
                Positivo = días pendientes del año anterior. Negativo = deuda a restar del cupo actual.
                {ficha.vacaciones.arrastre_origen === "manual"
                  ? " Hay un override manual activo."
                  : " Ahora se calcula automáticamente del saldo del año anterior."}
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Días de arrastre</Label>
                  <Input
                    type="number"
                    min={-365}
                    max={365}
                    value={arrastreForm.dias}
                    onChange={(e) => setArrastreForm({ ...arrastreForm, dias: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Motivo</Label>
                  <Input
                    value={arrastreForm.motivo}
                    onChange={(e) => setArrastreForm({ ...arrastreForm, motivo: e.target.value })}
                    placeholder="Ej. Remanente pactado / compensación"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={guardandoArrastre}
                  onClick={guardarArrastre}
                >
                  {guardandoArrastre ? "Guardando…" : "Fijar arrastre"}
                </Button>
                {ficha.vacaciones.arrastre_origen === "manual" && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={guardandoArrastre}
                    onClick={quitarArrastre}
                  >
                    Quitar override (usar cálculo auto)
                  </Button>
                )}
              </div>
            </div>
          )}
          {esAdmin && (
            <div className="mt-4 space-y-3 border-t border-indigo-100 pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Días ya consumidos al incorporar ({ficha.vacaciones?.anio || new Date().getFullYear()})
              </p>
              <p className="text-xs text-zinc-500">
                Para altas a mitad de año: el trabajador hereda el cupo de la empresa (p. ej. 22),
                pero aquí se indican los días que ya disfrutó fuera del sistema. Solo restan el
                saldo de este año; en enero el cupo se renueva y el remanente pasa por arrastre
                (no se vuelven a contar como usados).
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Vacaciones usadas</Label>
                  <Input
                    type="number"
                    min={0}
                    max={366}
                    value={usadosForm.vacaciones}
                    onChange={(e) => setUsadosForm({ ...usadosForm, vacaciones: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Asuntos propios usados</Label>
                  <Input
                    type="number"
                    min={0}
                    max={366}
                    value={usadosForm.asuntos}
                    onChange={(e) => setUsadosForm({ ...usadosForm, asuntos: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Motivo</Label>
                  <Input
                    value={usadosForm.motivo}
                    onChange={(e) => setUsadosForm({ ...usadosForm, motivo: e.target.value })}
                    placeholder="Ej. Consumidos antes del alta en Ventura"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" disabled={guardandoUsados} onClick={guardarUsados}>
                  {guardandoUsados ? "Guardando…" : "Guardar días usados"}
                </Button>
                {(Number(ficha.vacaciones?.usados_ajuste || 0) > 0
                  || Number(ficha.asuntos_propios?.usados_ajuste || 0) > 0) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={guardandoUsados}
                    onClick={quitarUsados}
                  >
                    Quitar ajuste de este año
                  </Button>
                )}
              </div>
            </div>
          )}
        </SeccionCard>

        {ficha.asuntos_propios?.activo && (
          <SeccionCard icon={FileText} titulo="Asuntos propios" accent="rose">
            <AsuntosPropiosResumen asuntos={ficha.asuntos_propios} usuario={u} />
          </SeccionCard>
        )}
      </div>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-h-[85vh] w-[95vw] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial de contratos y condiciones</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {historial.map((h) => (
              <div key={h.id} className="rounded-xl border border-zinc-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-zinc-900">
                      {fmtFecha(h.vigente_desde)}
                      {h.vigente_hasta ? ` — ${fmtFecha(h.vigente_hasta)}` : " — actual"}
                    </p>
                    <p className="text-xs text-zinc-500">{h.motivo}</p>
                    {h.registrado_por && (
                      <p className="text-[11px] text-zinc-400">Registrado por {h.registrado_por}</p>
                    )}
                  </div>
                  {h.vigente && <Badge className="bg-teal-600">Vigente</Badge>}
                </div>
                <div className="mt-3 grid gap-2 text-sm text-zinc-600 sm:grid-cols-2 lg:grid-cols-4">
                  <span>{h.datos?.tipo_contrato_nombre || h.datos?.tipo_jornada || "—"}</span>
                  <span>
                    Antigüedad:{" "}
                    {h.datos?.fecha_inicio_contrato ? fmtFecha(h.datos.fecha_inicio_contrato) : "—"}
                  </span>
                  <span>{h.datos?.puesto_nombre || "—"}</span>
                  <span>{h.datos?.departamento_nombre || "—"}</span>
                </div>
              </div>
            ))}
            {historial.length === 0 && (
              <p className="py-8 text-center text-zinc-400">Sin historial registrado.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={nuevaOpen} onOpenChange={setNuevaOpen}>
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cambio de contrato</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs text-indigo-900">
              Se cierra el periodo actual y se abre uno nuevo. Por defecto se mantiene la{" "}
              <strong>antigüedad</strong> (inicio de contrato); no la confundas con la fecha en que
              empiezan las nuevas condiciones.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Efectivas desde</Label>
                <Input type="date" value={form.vigente_desde} onChange={(e) => setForm({ ...form, vigente_desde: e.target.value })} />
                <p className="text-[11px] text-zinc-500">
                  Desde cuándo aplica el nuevo tipo de contrato / jornada (ej. cambio a 8 h).
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Inicio de contrato (antigüedad)</Label>
                <Input
                  type="date"
                  value={form.fecha_inicio_contrato}
                  onChange={(e) => setForm({ ...form, fecha_inicio_contrato: e.target.value })}
                />
                <p className="text-[11px] text-zinc-500">
                  Solo cámbiala si corriges el alta real. Afecta al prorrateo de vacaciones.
                </p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Motivo del cambio</Label>
              <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Ej. Ampliación de jornada de 4 h a 8 h…" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Tipo de contrato</Label>
                <Select value={form.tipo_contrato_id} onValueChange={(v) => setForm({ ...form, tipo_contrato_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cambio</SelectItem>
                    {tiposContrato.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Departamento</Label>
                <Select value={form.departamento_id} onValueChange={(v) => setForm({ ...form, departamento_id: v, puesto_id: "none" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cambio</SelectItem>
                    {deps.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Puesto</Label>
                <Select value={form.puesto_id} onValueChange={(v) => setForm({ ...form, puesto_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cambio</SelectItem>
                    {puestos
                      .filter((p) => form.departamento_id === "none" || p.departamento_id === Number(form.departamento_id))
                      .map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3 rounded-lg border border-zinc-100 bg-zinc-50/60 p-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="horario-ind"
                  checked={usarHorario}
                  onChange={(e) => onToggleHorarioIndividual(e.target.checked)}
                />
                <Label htmlFor="horario-ind">Actualizar horario individual</Label>
              </div>
              <p className="text-[11px] text-zinc-500">
                El horario anterior se conserva hasta el día previo a «Efectivas desde»; el nuevo
                aplica a partir de esa fecha (no recalcula déficit de horas en el pasado).
              </p>
              {!usarHorario && (
                <p className="text-xs text-zinc-500">
                  Hereda del departamento: {resumenModoHorario(horarioDepartamento(deps, u?.departamento_id ? String(u.departamento_id) : "none"))}
                  {" · "}
                  {resumenHorarioTramos(horarioDepartamento(deps, u?.departamento_id ? String(u.departamento_id) : "none"))}
                </p>
              )}
              {usarHorario && (
                <>
                  <p className="text-xs text-zinc-500">
                    Configuración cargada desde el departamento o el horario actual del trabajador.
                  </p>
                  <HorarioModoConfig config={horarioModo} onChange={setHorarioModo} plantillas={turnos} />
                  {horarioModo.modo !== MODO_PATRON_TURNOS && (
                    <HorarioTramosEditor
                      tramos={horarioTramos}
                      onChange={setHorarioTramos}
                      prefix="ficha-empleado-tramos"
                      plantillas={turnos}
                      modo={horarioModo.modo}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuevaOpen(false)}>Cancelar</Button>
            <Button onClick={guardarCondiciones} disabled={guardando} className="bg-indigo-600 hover:bg-indigo-700">
              {guardando ? "Guardando…" : "Registrar cambio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmAntiguedadOpen} onOpenChange={setConfirmAntiguedadOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cambiar la antigüedad?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a modificar el inicio de contrato
              {antiguedadOriginal ? ` (de ${fmtFecha(antiguedadOriginal)}` : ""}
              {form.fecha_inicio_contrato ? ` a ${fmtFecha(form.fecha_inicio_contrato)})` : ")"}.
              Eso recalcula el prorrateo de vacaciones del año. Si solo amplías jornada (p. ej. 4 h → 8 h),
              deja la antigüedad igual y usa «Efectivas desde».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={guardando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={guardando}
              onClick={(e) => {
                e.preventDefault();
                enviarCondiciones(true);
              }}
            >
              Sí, cambiar antigüedad
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
